import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SyncIcon from '@mui/icons-material/Sync';
import { format } from 'date-fns';
import { Link as RouterLink } from 'react-router-dom';
import Link from '@mui/material/Link';
import useAuthorize from '../hooks/useAuthorize';
import { DASHBOARD_MODULE_ROLES } from '../roles/portalRoles';
import SocialCampaignFunnelChart from './SocialCampaignFunnelChart';
import {
  fetchMarketingActivities,
  fetchSocialCampaigns,
  mergeCampaignSpend,
  saveMarketingActivity,
  syncStoreAcquisitions as syncStoreAcquisitionsApi,
  SocialCampaignOption,
  SocialCampaignRow,
  SocialCampaignResponse,
  StoreSyncStatus,
} from './socialCampaignApi';

const formatInr = (value: number) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(value || 0);

const pct = (num: number, den: number) => {
  if (!den) return '0%';
  return `${((num / den) * 100).toFixed(1)}%`;
};

const emptySpendForm = {
  id: undefined as number | undefined,
  activity_name: '',
  utm_campaign: '',
  utm_source: '',
  utm_medium: '',
  channel: 'facebook',
  budget_amount: '',
  notes: '',
};

const DashboardSocial = () => {
  useAuthorize(DASHBOARD_MODULE_ROLES);

  const getDefaultFromDate = () => {
    const now = new Date();
    return format(new Date(now.getFullYear(), 8, 1), 'yyyy-MM-dd');
  };

  const [fromDate, setFromDate] = useState(getDefaultFromDate());
  const [toDate, setToDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [campaigns, setCampaigns] = useState<SocialCampaignRow[]>([]);
  const [campaignOptions, setCampaignOptions] = useState<SocialCampaignOption[]>([]);
  const [storeSyncStatus, setStoreSyncStatus] = useState<StoreSyncStatus[]>([]);
  const [syncingStores, setSyncingStores] = useState(false);
  const [storeSyncMessage, setStoreSyncMessage] = useState<string | null>(null);
  const [totals, setTotals] = useState({
    redirect_landings: 0,
    store_redirects: 0,
    attributed_installs: 0,
    store_listing_visitors: 0,
    google_play_acquisitions: 0,
    apple_first_time_downloads: 0,
    store_acquisitions: 0,
    total_downloads: 0,
    visits: 0,
    registered: 0,
    wishlist: 0,
    inquiries: 0,
    orders: 0,
    inquiry_value_total: 0,
    order_value_total: 0,
    lead_value_total: 0,
  });
  const [selectedCampaign, setSelectedCampaign] = useState<SocialCampaignRow | null>(null);
  const [spendDialogOpen, setSpendDialogOpen] = useState(false);
  const [spendForm, setSpendForm] = useState(emptySpendForm);
  const [savingSpend, setSavingSpend] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      let socialData: SocialCampaignResponse = {
        campaigns: [],
        totals: {
          redirect_landings: 0,
          store_redirects: 0,
          attributed_installs: 0,
          store_listing_visitors: 0,
          google_play_acquisitions: 0,
          apple_first_time_downloads: 0,
          store_acquisitions: 0,
          total_downloads: 0,
          visits: 0,
          registered: 0,
          wishlist: 0,
          inquiries: 0,
          orders: 0,
          inquiry_value_total: 0,
          order_value_total: 0,
          lead_value_total: 0,
        },
      };

      try {
        socialData = await fetchSocialCampaigns(fromDate, toDate);
      } catch (err: any) {
        const status = err?.response?.status;
        if (status === 401) {
          setError('Your session may have expired. Please sign out and sign in again, then reload this page.');
        } else {
          setError(err?.response?.data?.message || err.message || 'Failed to load social campaign analytics');
        }
      }

      const activities = await fetchMarketingActivities().catch(() => []);
      const merged = mergeCampaignSpend(socialData.campaigns, activities);
      setCampaigns(merged);
      setCampaignOptions(socialData.campaign_options || []);
      setStoreSyncStatus(socialData.store_sync_status || []);
      setTotals({
        redirect_landings: socialData.totals.redirect_landings || 0,
        store_redirects: socialData.totals.store_redirects || 0,
        attributed_installs: socialData.totals.attributed_installs || 0,
        store_listing_visitors: socialData.totals.store_listing_visitors || 0,
        google_play_acquisitions: socialData.totals.google_play_acquisitions || 0,
        apple_first_time_downloads: socialData.totals.apple_first_time_downloads || 0,
        store_acquisitions: socialData.totals.store_acquisitions || 0,
        total_downloads: socialData.totals.total_downloads || 0,
        visits: socialData.totals.visits || 0,
        registered: socialData.totals.registered || 0,
        wishlist: socialData.totals.wishlist || 0,
        inquiries: socialData.totals.inquiries || 0,
        orders: socialData.totals.orders || 0,
        inquiry_value_total: socialData.totals.inquiry_value_total || 0,
        order_value_total: socialData.totals.order_value_total || 0,
        lead_value_total:
          (socialData.totals.inquiry_value_total || 0) + (socialData.totals.order_value_total || 0),
      });
    } catch (err: any) {
      setError(err.message || 'Failed to load social campaign analytics');
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [fromDate, toDate]);

  const handleStoreSync = async () => {
    try {
      setSyncingStores(true);
      setStoreSyncMessage(null);
      const results = await syncStoreAcquisitionsApi(fromDate, toDate);
      setStoreSyncMessage(
        results
          .map((result) => {
            const provider = result.provider === 'google_play' ? 'Google Play' : 'App Store';
            if (result.status === 'not_configured') return `${provider}: credentials not configured`;
            if (result.status === 'pending') return `${provider}: report generation pending`;
            if (result.status === 'error') return `${provider}: ${result.message || 'sync failed'}`;
            return `${provider}: ${result.rowsImported || 0} report rows imported`;
          })
          .join(' · ')
      );
      await loadData();
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Store report sync failed');
    } finally {
      setSyncingStores(false);
    }
  };

  const portfolioRoi = useMemo(() => {
    const spend = campaigns.reduce((sum, row) => sum + Number(row.budget_amount || 0), 0);
    const leadValue = totals.lead_value_total || 0;
    if (!spend) return null;
    return {
      spend,
      leadValue,
      roiPercent: Number((((leadValue - spend) / spend) * 100).toFixed(2)),
      roas: Number((leadValue / spend).toFixed(2)),
    };
  }, [campaigns, totals.lead_value_total]);

  const selectedFunnelTotals = selectedCampaign
    ? {
        redirect_landings: selectedCampaign.redirect_landings || 0,
        store_redirects: selectedCampaign.store_redirects || 0,
        attributed_installs:
          selectedCampaign.store_acquisitions || selectedCampaign.attributed_installs || 0,
        visits: selectedCampaign.visits,
        inquiries: selectedCampaign.inquiries,
      }
    : {
        redirect_landings: totals.redirect_landings || 0,
        store_redirects: totals.store_redirects || 0,
        attributed_installs: totals.store_acquisitions || totals.attributed_installs || 0,
        visits: totals.visits,
        inquiries: totals.inquiries,
      };

  const spendCampaignOptions = useMemo(() => {
    const map = new Map<string, SocialCampaignOption>();

    campaignOptions.forEach((option) => {
      map.set(option.utm_campaign, option);
    });

    campaigns.forEach((campaign) => {
      if (!campaign.utm_campaign || map.has(campaign.utm_campaign)) {
        return;
      }
      map.set(campaign.utm_campaign, {
        utm_source: campaign.utm_source,
        utm_medium: campaign.utm_medium,
        utm_campaign: campaign.utm_campaign,
        event_count:
          (campaign.redirect_landings || 0) +
          (campaign.store_redirects || 0) +
          (campaign.visits || 0),
      });
    });

    return Array.from(map.values()).sort((a, b) => b.event_count - a.event_count);
  }, [campaignOptions, campaigns]);

  const selectedSpendOption = useMemo(
    () => spendCampaignOptions.find((option) => option.utm_campaign === spendForm.utm_campaign) || null,
    [spendCampaignOptions, spendForm.utm_campaign]
  );

  const openSpendDialog = (campaign?: SocialCampaignRow) => {
    setSpendForm({
      ...emptySpendForm,
      id: campaign?.marketing_activity_id || undefined,
      activity_name: campaign?.utm_campaign || '',
      utm_campaign: campaign?.utm_campaign || '',
      utm_source: campaign?.utm_source || '',
      utm_medium: campaign?.utm_medium || '',
      budget_amount: campaign?.budget_amount ? String(campaign.budget_amount) : '',
      notes: campaign?.spend_notes || '',
    });
    setSpendDialogOpen(true);
  };

  const handleCampaignPick = (option: SocialCampaignOption | null) => {
    if (!option) {
      setSpendForm((current) => ({
        ...current,
        utm_campaign: '',
        activity_name: '',
      }));
      return;
    }

    const existing = campaigns.find((row) => row.utm_campaign === option.utm_campaign);
    setSpendForm((current) => ({
      ...current,
      id: existing?.marketing_activity_id || current.id,
      utm_campaign: option.utm_campaign,
      activity_name: option.utm_campaign,
      utm_source: option.utm_source,
      utm_medium: option.utm_medium,
      budget_amount: existing?.budget_amount ? String(existing.budget_amount) : current.budget_amount,
      notes: existing?.spend_notes || current.notes,
    }));
  };

  const handleSaveSpend = async () => {
    if (!spendForm.utm_campaign) {
      return;
    }

    const hasSpendData = Boolean(spendForm.budget_amount || spendForm.notes || spendForm.id);
    if (!hasSpendData) {
      setSpendDialogOpen(false);
      return;
    }

    try {
      setSavingSpend(true);
      await saveMarketingActivity({
        id: spendForm.id,
        activity_name: spendForm.activity_name || spendForm.utm_campaign,
        utm_campaign: spendForm.utm_campaign,
        utm_source: spendForm.utm_source || null,
        utm_medium: spendForm.utm_medium || null,
        channel: spendForm.channel,
        campaign_name: spendForm.utm_campaign,
        budget_amount: spendForm.budget_amount ? Number(spendForm.budget_amount) : null,
        status: 'active',
        activity_type: 'paid_social',
        notes: spendForm.notes || null,
      });
      setSpendDialogOpen(false);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to save campaign spend');
    } finally {
      setSavingSpend(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <ShareIcon color="primary" />
        <Typography variant="h5">Social Campaigns</Typography>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Track Facebook and paid social campaigns from redirect landing through store-reported
        acquisition, web visit, inquiry, and order. Google Play and App Store figures are aggregate
        campaign reports and do not identify individual customers.
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        Redirect and store tracking started on <strong>5 Sep 2026</strong>. Facebook link clicks before
        that are not in this report. Use the date range to match when traffic was tracked. Point Facebook
        app-download ads to{' '}
        <code>
          https://app.credcosourcing.com/app-download.html?utm_source=facebook&utm_medium=paid_social&utm_campaign=App_Inquiry_Lead
        </code>
        . For <strong>marketplace seller signup</strong>, use{' '}
        <Link component={RouterLink} to="/dashboard/seller-acquisition">
          Marketplace sellers funnel
        </Link>
        .
      </Alert>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4} md={3}>
          <TextField
            label="From"
            type="date"
            fullWidth
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <TextField
            label="To"
            type="date"
            fullWidth
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <Button variant="outlined" onClick={() => openSpendDialog()} sx={{ height: '56px' }}>
            Add / edit spend
          </Button>
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <Button
            variant="contained"
            startIcon={<SyncIcon />}
            disabled={syncingStores}
            onClick={handleStoreSync}
            sx={{ height: '56px' }}
          >
            {syncingStores ? 'Syncing stores…' : 'Sync store acquisitions'}
          </Button>
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {storeSyncMessage && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {storeSyncMessage}
        </Alert>
      )}

      {storeSyncStatus.some((item) => item.status === 'not_configured') && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Store reporting is deployed but one or more provider credentials are not configured.
          Google Play reports can arrive 3–7 days late; Apple campaign reports require at least
          five first-time downloads and normally appear after 24 hours.
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {[
              ['Redirect landings', totals.redirect_landings || 0],
              ['Store handoffs', totals.store_redirects || 0],
              ['Play acquired users', totals.google_play_acquisitions || 0],
              ['Apple first downloads', totals.apple_first_time_downloads || 0],
              ['Total store acquired', totals.store_acquisitions || 0],
              ['Web visits', totals.visits],
              ['Inquiries', totals.inquiries],
              ['Lead value', formatInr(totals.lead_value_total)],
            ].map(([label, value]) => (
              <Grid item xs={12} sm={6} md={3} key={String(label)}>
                <Card>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">
                      {label}
                    </Typography>
                    <Typography variant="h6">{value}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {portfolioRoi && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Portfolio ROI: {portfolioRoi.roiPercent}% · ROAS {portfolioRoi.roas}x · Spend{' '}
              {formatInr(portfolioRoi.spend)} · Lead value {formatInr(portfolioRoi.leadValue)}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} lg={7}>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Campaign</TableCell>
                      <TableCell>Source / Medium</TableCell>
                      <TableCell align="right">Redirect</TableCell>
                      <TableCell align="right">Store</TableCell>
                      <TableCell align="right">Store acquired</TableCell>
                      <TableCell align="right">Web visits</TableCell>
                      <TableCell align="right">Inquiries</TableCell>
                      <TableCell align="right">Lead value</TableCell>
                      <TableCell align="right">Spend</TableCell>
                      <TableCell align="right">ROI</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {campaigns.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10}>
                          No campaign activity in this date range.
                          {campaignOptions.length > 0
                            ? ` ${campaignOptions.length} campaigns are tracked overall — widen the date range (try year-to-date) or wait for new redirect clicks after 5 Sep 2026.`
                            : ' Send paid traffic with UTM parameters to see results here.'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      campaigns.map((row) => (
                        <TableRow
                          key={`${row.utm_campaign}-${row.utm_source}`}
                          hover
                          selected={selectedCampaign?.utm_campaign === row.utm_campaign}
                          onClick={() => setSelectedCampaign(row)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>{row.utm_campaign}</TableCell>
                          <TableCell>
                            {row.utm_source} / {row.utm_medium}
                          </TableCell>
                          <TableCell align="right">{row.redirect_landings || 0}</TableCell>
                          <TableCell align="right">{row.store_redirects || 0}</TableCell>
                          <TableCell align="right">{row.store_acquisitions || 0}</TableCell>
                          <TableCell align="right">{row.visits}</TableCell>
                          <TableCell align="right">
                            {row.inquiries} ({pct(row.inquiries, row.visits || row.redirect_landings || 0)})
                          </TableCell>
                          <TableCell align="right">{formatInr(row.lead_value_total)}</TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                              <span>{row.budget_amount ? formatInr(row.budget_amount) : '—'}</span>
                              <IconButton
                                size="small"
                                aria-label={`Edit spend for ${row.utm_campaign}`}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  openSpendDialog(row);
                                }}
                              >
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          </TableCell>
                          <TableCell align="right">
                            {row.roi_percent != null ? `${row.roi_percent}%` : '—'}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            <Grid item xs={12} lg={5}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    {selectedCampaign
                      ? `Funnel — ${selectedCampaign.utm_campaign}`
                      : 'Portfolio funnel'}
                  </Typography>
                  <SocialCampaignFunnelChart totals={selectedFunnelTotals} />
                  {selectedCampaign && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Redirect landings: {selectedCampaign.redirect_landings || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Store handoffs observed: {selectedCampaign.store_redirects || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Store-reported acquired users: {selectedCampaign.store_acquisitions || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Google Play acquisitions: {selectedCampaign.google_play_acquisitions || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Apple first-time downloads: {selectedCampaign.apple_first_time_downloads || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Native Android first opens: {selectedCampaign.attributed_installs || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Inquiry value: {formatInr(selectedCampaign.inquiry_value_total)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Order value: {formatInr(selectedCampaign.order_value_total)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Visit → inquiry: {selectedCampaign.visit_to_inquiry_rate}%
                      </Typography>
                      <Button size="small" sx={{ mt: 1 }} onClick={() => openSpendDialog(selectedCampaign)}>
                        Add / edit spend
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}

      <Dialog open={spendDialogOpen} onClose={() => setSpendDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{spendForm.id ? 'Edit campaign spend' : 'Add campaign spend'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Campaigns are auto-filled from redirect/referrer tracking and web visits. Pick one below,
              then add spend when you have it. You can come back and edit later.
            </Typography>
            <Autocomplete
              options={spendCampaignOptions}
              value={selectedSpendOption}
              onChange={(_event, option) => handleCampaignPick(option)}
              getOptionLabel={(option) => option.utm_campaign}
              isOptionEqualToValue={(option, value) => option.utm_campaign === value.utm_campaign}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Campaign"
                  required
                  helperText={
                    spendCampaignOptions.length
                      ? `${spendCampaignOptions.length} tracked campaigns available`
                      : 'No tracked campaigns yet'
                  }
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={`${option.utm_campaign}-${option.utm_source}`}>
                  {option.utm_campaign} ({option.utm_source} / {option.utm_medium})
                </li>
              )}
            />
            <TextField
              label="UTM source"
              value={spendForm.utm_source}
              onChange={(e) => setSpendForm({ ...spendForm, utm_source: e.target.value })}
              fullWidth
              disabled={Boolean(selectedSpendOption)}
            />
            <TextField
              label="UTM medium"
              value={spendForm.utm_medium}
              onChange={(e) => setSpendForm({ ...spendForm, utm_medium: e.target.value })}
              fullWidth
              disabled={Boolean(selectedSpendOption)}
            />
            <TextField
              label="Budget / spend (INR)"
              type="number"
              value={spendForm.budget_amount}
              onChange={(e) => setSpendForm({ ...spendForm, budget_amount: e.target.value })}
              fullWidth
              helperText="Optional now — you can save and add spend later"
            />
            <TextField
              label="Notes"
              value={spendForm.notes}
              onChange={(e) => setSpendForm({ ...spendForm, notes: e.target.value })}
              fullWidth
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSpendDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveSpend}
            disabled={savingSpend || !spendForm.utm_campaign}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DashboardSocial;
